
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import tradingSalesOrderService from "../services/tradingSalesOrderService";

const styles = {
  page: {
    padding: "24px",
    background: "#f7f8fc",
    minHeight: "100%",
    fontFamily: "Inter, Arial, sans-serif",
    color: "#252b3b",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "16px",
    flexWrap: "wrap",
    marginBottom: "22px",
  },
  title: { margin: 0, fontSize: "25px", fontWeight: 700 },
  subtitle: {
    margin: "7px 0 0",
    color: "#747b8d",
    fontSize: "13px",
  },
  button: {
    border: 0,
    borderRadius: "8px",
    padding: "11px 17px",
    background: "#6254d9",
    color: "#fff",
    fontWeight: 600,
    cursor: "pointer",
  },
  secondaryButton: {
    border: "1px solid #dfe2eb",
    borderRadius: "8px",
    padding: "10px 14px",
    background: "#fff",
    color: "#374151",
    fontWeight: 600,
    cursor: "pointer",
  },
  card: {
    background: "#fff",
    border: "1px solid #e8eaf2",
    borderRadius: "12px",
    padding: "18px",
  },
  filters: {
    display: "flex",
    gap: "12px",
    flexWrap: "wrap",
    marginBottom: "16px",
  },
  input: {
    minWidth: "220px",
    flex: 1,
    border: "1px solid #dfe2eb",
    borderRadius: "8px",
    padding: "11px 12px",
    fontSize: "13px",
    outline: "none",
    boxSizing: "border-box",
  },
  tableWrap: { width: "100%", overflowX: "auto" },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    minWidth: "1050px",
  },
  th: {
    textAlign: "left",
    padding: "13px 12px",
    background: "#f7f8fc",
    color: "#70778a",
    fontSize: "11px",
    textTransform: "uppercase",
    letterSpacing: ".4px",
    borderBottom: "1px solid #e8eaf2",
    whiteSpace: "nowrap",
  },
  td: {
    padding: "14px 12px",
    fontSize: "13px",
    borderBottom: "1px solid #f0f1f6",
    whiteSpace: "nowrap",
  },
  radioCell: {
    textAlign: "center",
    width: "45px",
  },
  badge: {
    display: "inline-block",
    borderRadius: "20px",
    padding: "5px 9px",
    fontSize: "11px",
    fontWeight: 700,
    background: "#dcfce7",
    color: "#166534",
    whiteSpace: "nowrap",
  },
  error: {
    padding: "12px 14px",
    background: "#fff0f0",
    color: "#b42318",
    borderRadius: "8px",
    fontSize: "13px",
    marginBottom: "16px",
  },
  info: {
    padding: "12px 14px",
    background: "#eef4ff",
    color: "#344b82",
    borderRadius: "8px",
    fontSize: "13px",
    marginBottom: "16px",
  },
  muted: {
    color: "#858b9b",
    fontSize: "13px",
    textAlign: "center",
    padding: "32px 12px",
  },
  footer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "12px",
    flexWrap: "wrap",
    marginTop: "18px",
  },
};

const formatDate = (value) => {
  if (!value) return "—";

  const raw = String(value).slice(0, 10);
  const parts = raw.split("-");

  return parts.length === 3
    ? `${parts[2]}-${parts[1]}-${parts[0]}`
    : raw;
};

const formatAmount = (value, currency = "INR") => {
  const amount = Number(value ?? 0);

  if (!Number.isFinite(amount)) return "—";

  try {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: currency || "INR",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    return amount.toFixed(2);
  }
};

const getResponseData = (response) =>
  response?.data?.data ?? response?.data ?? {};

const getQuotationRows = (body) => {
  if (Array.isArray(body)) return body;
  if (Array.isArray(body?.items)) return body.items;
  if (Array.isArray(body?.quotations)) return body.quotations;
  return [];
};

export default function TradingSalesOrderSelectQuotationPage() {
  const navigate = useNavigate();

  const [quotations, setQuotations] = useState([]);
  const [selectedQuotationId, setSelectedQuotationId] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");

  const loadQuotations = useCallback(async () => {
    setLoading(true);
    setError("");
    setInfo("");
    setSelectedQuotationId("");

    try {
      const response =
        await tradingSalesOrderService.getQuotationsEligibleForSalesOrder();

      const body = getResponseData(response);
      const rows = getQuotationRows(body);

      setQuotations(rows);
    } catch (err) {
      setQuotations([]);
      setError(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          err?.message ||
          "Unable to load quotations eligible for Sales Order creation."
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadQuotations();
  }, [loadQuotations]);

  const filteredQuotations = useMemo(() => {
    const term = search.trim().toLowerCase();

    if (!term) return quotations;

    return quotations.filter((row) =>
      [
        row.quotation_no,
        row.customer_name,
        row.customer_code,
        row.enquiry_no,
        row.costing_no,
        row.customer_reference,
        row.subject,
      ].some((value) => String(value || "").toLowerCase().includes(term))
    );
  }, [quotations, search]);

  const selectedQuotation = useMemo(
    () =>
      quotations.find(
        (row) => String(row.id) === String(selectedQuotationId)
      ) || null,
    [quotations, selectedQuotationId]
  );

  const createSalesOrder = async () => {
    if (!selectedQuotation) {
      setError("Please select a quotation first.");
      return;
    }

    setCreating(true);
    setError("");
    setInfo("");

    try {
      const response =
        await tradingSalesOrderService.createSalesOrderFromQuotation(
          selectedQuotation.id,
          {}
        );

      const body = getResponseData(response);
      const salesOrder =
        body?.sales_order ||
        body?.salesOrder ||
        body?.sales_order_header ||
        body;

      const salesOrderId =
        salesOrder?.id ||
        body?.sales_order_id ||
        body?.salesOrderId;

      if (!salesOrderId) {
        setInfo(
          "The request completed, but the Sales Order ID was not returned. " +
            "Please check the API response before retrying."
        );
        return;
      }

      navigate(`/trading/sales-orders/${salesOrderId}`);
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          err?.message ||
          "Unable to create the Sales Order from this quotation."
      );
    } finally {
      setCreating(false);
    }
  };

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Select Quotation</h1>
          <p style={styles.subtitle}>
            Select a customer-approved quotation to create a Trading Sales
            Order.
          </p>
        </div>

        <button
          type="button"
          style={styles.secondaryButton}
          onClick={() => navigate("/trading/sales-orders")}
          disabled={creating}
        >
          Back to Sales Orders
        </button>
      </div>

      {error && (
        <div role="alert" style={styles.error}>
          {error}
        </div>
      )}

      {info && (
        <div role="status" style={styles.info}>
          {info}
        </div>
      )}

      <div style={styles.card}>
        <div style={styles.filters}>
          <input
            type="text"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search quotation, customer, enquiry or costing..."
            style={styles.input}
            aria-label="Search eligible quotations"
          />

          <button
            type="button"
            style={{
              ...styles.secondaryButton,
              opacity: loading || creating ? 0.6 : 1,
              cursor: loading || creating ? "not-allowed" : "pointer",
            }}
            onClick={loadQuotations}
            disabled={loading || creating}
          >
            {loading ? "Loading..." : "Refresh"}
          </button>
        </div>

        <div style={styles.tableWrap}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Select</th>
                <th style={styles.th}>Quotation No.</th>
                <th style={styles.th}>Version</th>
                <th style={styles.th}>Customer</th>
                <th style={styles.th}>Enquiry No.</th>
                <th style={styles.th}>Costing No.</th>
                <th style={styles.th}>Quotation Date</th>
                <th style={styles.th}>Valid Until</th>
                <th style={styles.th}>Currency</th>
                <th style={styles.th}>Net Amount</th>
                <th style={styles.th}>Status</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={11} style={styles.muted}>
                    Loading eligible quotations...
                  </td>
                </tr>
              ) : filteredQuotations.length === 0 ? (
                <tr>
                  <td colSpan={11} style={styles.muted}>
                    {error
                      ? "Quotations could not be loaded."
                      : search.trim()
                        ? "No quotations match your search."
                        : "No eligible quotations are available for Sales Order creation."}
                  </td>
                </tr>
              ) : (
                filteredQuotations.map((row) => (
                  <tr
                    key={row.id}
                    onClick={() => setSelectedQuotationId(String(row.id))}
                    style={{
                      background:
                        String(row.id) === String(selectedQuotationId)
                          ? "#f3f0ff"
                          : "#fff",
                      cursor: "pointer",
                    }}
                  >
                    <td style={styles.td}>
                      <div style={styles.radioCell}>
                        <input
                          type="radio"
                          name="selectedQuotation"
                          aria-label={`Select quotation ${row.quotation_no || row.id}`}
                          checked={
                            String(row.id) === String(selectedQuotationId)
                          }
                          onChange={() =>
                            setSelectedQuotationId(String(row.id))
                          }
                        />
                      </div>
                    </td>

                    <td style={styles.td}>
                      <strong>{row.quotation_no || `QT-${row.id}`}</strong>
                    </td>

                    <td style={styles.td}>
                      {row.version_no ?? "—"}
                    </td>

                    <td style={styles.td}>
                      {row.customer_name || row.customer_code || "—"}
                    </td>

                    <td style={styles.td}>{row.enquiry_no || "—"}</td>

                    <td style={styles.td}>{row.costing_no || "—"}</td>

                    <td style={styles.td}>
                      {formatDate(row.quotation_date)}
                    </td>

                    <td style={styles.td}>
                      {formatDate(row.valid_until)}
                    </td>

                    <td style={styles.td}>{row.currency_code || "INR"}</td>

                    <td style={styles.td}>
                      {formatAmount(row.net_amount, row.currency_code)}
                    </td>

                    <td style={styles.td}>
                      <span style={styles.badge}>Approved by Customer</span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div style={styles.footer}>
          <span style={{ color: "#747b8d", fontSize: "12px" }}>
            {loading
              ? "Retrieving quotations..."
              : `${filteredQuotations.length} quotation(s) available`}
            {selectedQuotation ? " · 1 selected" : ""}
          </span>

          <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
            <button
              type="button"
              style={styles.secondaryButton}
              onClick={() => setSelectedQuotationId("")}
              disabled={!selectedQuotationId || creating}
            >
              Clear Selection
            </button>

            <button
              type="button"
              style={{
                ...styles.button,
                opacity:
                  !selectedQuotation || creating || loading ? 0.6 : 1,
                cursor:
                  !selectedQuotation || creating || loading
                    ? "not-allowed"
                    : "pointer",
              }}
              onClick={createSalesOrder}
              disabled={!selectedQuotation || creating || loading}
            >
              {creating ? "Creating Sales Order..." : "Create Sales Order"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
